import { Map, View } from 'ol';
import VectorSource from 'ol/source/Vector.js';
import { Circle, Fill, Stroke, Style } from 'ol/style.js'
import { DragBox, Select } from 'ol/interaction.js';
import { platformModifierKeyOnly } from 'ol/events/condition.js';
import Draw from 'ol/interaction/Draw.js';
import Snap from 'ol/interaction/Snap.js';
import DragAndDrop from 'ol/interaction/DragAndDrop.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Graticule from 'ol/layer/Graticule.js';


import { RandomColor } from './uitools';
import RasterSource from 'ol/source/Raster';
import {FullScreen, ScaleLine, defaults as defaultControls} from 'ol/control.js';


class MapContainer {
    map;
    currentControls = [];
    interactionManager;
    layerManager;

    constructor(mapID) {
        this.mapID = mapID;
        this.interactionManager = new InteractionManager(this);
        this.layerManager = new LayerManager(this);
    }

    exportPng(completeFunction) {
        if (!completeFunction) { return; }
        this.map.once('rendercomplete', function (e) {
            const map = e.target;
            const mapCanvas = document.createElement('canvas');
            const size = map.getSize();
            mapCanvas.width = size[0];
            mapCanvas.height = size[1];
            const mapContext = mapCanvas.getContext('2d');
            Array.prototype.forEach.call(
                map.getViewport().querySelectorAll('.ol-layer canvas, canvas.ol-layer'),
                function (canvas) {
                    if (canvas.width > 0) {
                        const opacity =
                            canvas.parentNode.style.opacity || canvas.style.opacity;
                        mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
                        let matrix;
                        const transform = canvas.style.transform;
                        if (transform) {
                            // Get the transform parameters from the style's transform matrix
                            matrix = transform
                                .match(/^matrix\(([^\(]*)\)$/)[1]
                                .split(',')
                                .map(Number);
                        } else {
                            matrix = [
                                parseFloat(canvas.style.width) / canvas.width,
                                0,
                                0,
                                parseFloat(canvas.style.height) / canvas.height,
                                0,
                                0,
                            ];
                        }
                        // Apply the transform to the export map context
                        CanvasRenderingContext2D.prototype.setTransform.apply(
                            mapContext,
                            matrix
                        );
                        const backgroundColor = canvas.parentNode.style.backgroundColor;
                        if (backgroundColor) {
                            mapContext.fillStyle = backgroundColor;
                            mapContext.fillRect(0, 0, canvas.width, canvas.height);
                        }
                        mapContext.drawImage(canvas, 0, 0);
                    }
                }
            );
            mapContext.globalAlpha = 1;
            mapContext.setTransform(1, 0, 0, 1, 0, 0);
            completeFunction(mapCanvas.toDataURL());
        });
        this.map.renderSync();
    }
}

class InteractionManager {
    currentInteractions = [];
    select = null;
    dragBox = null;
    draw = null;
    snap = null;
    dragAndDropInteraction = null;

    constructor(mapContainer) {
        this.mapContainer = mapContainer;
    }


    addInteraction(interaction) {
        this.currentInteractions.push(interaction);
        this.mapContainer.map.addInteraction(interaction);

    }
    removeInteractions() {
        this.currentInteractions.forEach((interaction) => this.mapContainer.map.removeInteraction(interaction));

    }
    removeInteraction(interaction) {
        const index = this.currentInteractions.indexOf(interaction);
        this.currentInteractions.splice(index, 1);
        this.mapContainer.map.removeInteraction(interaction);
    }

    //if layers is null then works on all layers. otherwise restricted to the provided layer.
    toggleSelect(layers = null) {
        if (this.select) {
            this.removeInteraction(this.select);
            this.removeInteraction(this.dragBox);
            this.select = this.dragBox = null;
            return;
        }

        if (!layers) {
            layers = this.mapContainer.layerManager.getVectorLayers();
        }

        const selectedStyle = new Style({
            fill: new Fill({
                color: 'rgba(255, 255, 255, 0.6)',
            }),
            stroke: new Stroke({
                color: 'rgba(255, 255, 255, 0.7)',
                width: 2,
            }),
        });

        this.select = new Select({
            style: function (feature) {
                const color = '#eeeeee';
                selectedStyle.getFill().setColor(color);
                return selectedStyle;
            },
            layers: layers
        });
        this.addInteraction(this.select);

        const selectedFeatures = this.select.getFeatures();
        // a DragBox interaction used to select features by drawing boxes
        this.dragBox = new DragBox({
            condition: platformModifierKeyOnly,
        });

        const mapRotation = this.mapContainer.map.getView().getRotation();
        this.dragBox.on('boxend', function () {
            const extent = this.getGeometry().getExtent();
            let boxFeatures = [];
            layers.forEach((layer) => {
                const layerBoxFeatures = layer.getSource()
                    .getFeaturesInExtent(extent)
                    .filter((feature) => feature.getGeometry().intersectsExtent(extent));
                boxFeatures = boxFeatures.concat(layerBoxFeatures);
            })

            // features that intersect the box geometry are added to the
            // collection of selected features

            // if the view is not obliquely rotated the box geometry and
            // its extent are equalivalent so intersecting features can
            // be added directly to the collection
            const oblique = mapRotation % (Math.PI / 2) !== 0;

            // when the view is obliquely rotated the box extent will
            // exceed its geometry so both the box and the candidate
            // feature geometries are rotated around a common anchor
            // to confirm that, with the box geometry aligned with its
            // extent, the geometries intersect
            if (oblique) {
                const anchor = [0, 0];
                const geometry = this.getGeometry().clone();
                geometry.rotate(-rotation, anchor);
                const extent = geometry.getExtent();
                boxFeatures.forEach(function (feature) {
                    const geometry = feature.getGeometry().clone();
                    geometry.rotate(-rotation, anchor);
                    if (geometry.intersectsExtent(extent)) {
                        selectedFeatures.push(feature);
                    }
                });
            } else {
                selectedFeatures.extend(boxFeatures);
            }
        });

        // clear selection when drawing a new box and when clicking on the map
        this.dragBox.on('boxstart', function () {
            selectedFeatures.clear();
        });

        this.addInteraction(this.dragBox);
    }

    toggleDraw(layer) {
        if (!layer) { return; }
        if (this.draw) {
            this.removeInteraction(this.draw);
            this.removeInteraction(this.snap);
            this.draw = this.snap = null;
            return;
        }


        this.draw = new Draw({
            type: 'Polygon',
            source: layer.getSource(),
            clickTolerance: 9,
            snapTolerance: 9,
            trace: true,
            traceSource: layer.getSource(),
            style: {
                'stroke-color': 'rgba(255, 255, 100, 0.5)',
                'stroke-width': 1.5,
                'fill-color': 'rgba(255, 255, 100, 0.25)',
                'circle-radius': 6,
                'circle-fill-color': 'rgba(255, 255, 100, 0.5)',
            },
        });
        this.snap = new Snap({
            source: layer.getSource(),
            pixelTolerance: 3
        });
        this.addInteraction(this.draw);
        this.addInteraction(this.snap);
    }

    toggleDragAndDrop(layer, stylePropertyName, legend = null) {
        if (!layer) { throw new Error("Define a layer"); }
        if (this.dragAndDropInteraction) {
            this.removeInteraction(this.dragAndDropInteraction);
            this.dragAndDropInteraction = null;
            return;
        }

        this.dragAndDropInteraction = new DragAndDrop({
            formatConstructors: [
                GeoJSON,
            ],
        });

        this.dragAndDropInteraction.on('addfeatures', (event) => {
            const vectorSource = new VectorSource({
                features: event.features,
            });
            if (legend) {
                legend.generatePolygonLegend(stylePropertyName, vectorSource);
            }
            const pointStyle = new Style({
                image: new Circle({
                    radius: 3,
                    fill: new Fill({
                        color: 'rgba(212, 0, 255, 0.6)',
                    }),
                    stroke: new Stroke({
                        color: 'rgba(255, 255, 255, 1.0)',
                        width: 1,
                    }),
                }),
                zIndex: Infinity,
            });
            vectorSource.forEachFeature((feature) => {
                const geometryType = feature.getGeometry().getType();
                switch (geometryType) {
                    case 'MultiPolygon':
                    case 'Polygon':
                        let propertyValue = feature.get(stylePropertyName);
                        if (!propertyValue) { propertyValue = 'unknown' }
                        if (propertyValue) {
                                feature.setStyle(legend.polyStyles[propertyValue].style);
                        }
                        break;
                    case 'Point':
                        feature.setStyle(pointStyle);
                        break;
                }
            });


            layer.setSource(vectorSource);
            this.mapContainer.map.getView().fit(vectorSource.getExtent());

        });

        this.addInteraction(this.dragAndDropInteraction);
    }
}

class LayerManager {
    currentLayers = [];
    nextImageLayerIndex = 0;

    constructor(mapContainer) {
        this.mapContainer = mapContainer;
    }

    addLayer(layer) {
        var name = layer.get('name');
        if (!name) {
            throw new Error("layer must contain a name.");
        }
        var layerType = layer.get('layerType');
        if (!layerType) {
            throw new Error("layer must contain layerType.");
        }

        if (this.mapContainer.map && this.getLayer(name)) { return new Error("Layer already exists" + name); }

        this.currentLayers.push(layer);

        if (!this.mapContainer.map) {
            var labelText = new Text({
                font: '12px Calibri,sans-serif',
                textBaseline: 'bottom',
                fill: new Fill({
                    color: 'rgba(0,0,0,1)'
                }),
                stroke: new Stroke({
                    color: 'rgba(255,255,255,1)',
                    width: 3
                })
            });
            const grat = new Graticule({
                // the style to use for the lines, optional.
                // strokeStyle: new Stroke({
                //     color: 'rgba(255,120,0,0.9)',
                //     width: 2,
                //     lineDash: [0.5, 4],
                // }),
                showLabels: true,
                wrapX: false,
                lonLabelPosition: 0.03,
                latLabelPosition: .98,
                });
            this.mapContainer.map = new Map({
                controls: defaultControls().extend([new FullScreen(), new ScaleLine()]),
                target: this.mapContainer.mapID,
                layers: [layer, grat],
                view: new View({
                    projection: 'EPSG:4326',
                    center: [0, 0],
                    zoom: 2,
                }),
            });
            if (layerType.includes('image')) {
                this.nextImageLayerIndex = 1;
            }
            return;
        }
        const layers = this.mapContainer.map.getLayers();
        if (layerType.includes('image')) {
            layers.insertAt(this.nextImageLayerIndex, layer);
            this.nextImageLayerIndex++;
        }
        else if (layerType.includes('vector')) {
            layers.push(layer);
        }
        else {
            throw new Error("Unknown layer type: " + layerType);
        }
    }

    getLayer(layerName) {
        const layers = this.mapContainer.map.getLayers().getArray();
        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            if (!layer) { return null; }
            var name = layer.get('name');
            if (name && name === layerName) {
                return layer;
            }
        }
        return null;
    }
    
    removeLayer(layerName) {
        const layer = this.getLayer(layerName);
        if (!layer) { return; }
        this.mapContainer.map.removeLayer(layer);
        if (layer.get('layerType').includes('image')) {
            this.nextImageLayerIndex--;
        }
    }

    getVectorLayers() {
        const layers = this.mapContainer.map.getLayers();
        const vectorLayers = [];
        layers.forEach((layer) => {
            const type = layer.get('layerType');
            if (type && type.includes('vector')) {
                vectorLayers.push(layer);
            }
        })
        return vectorLayers;
    }

}


export { MapContainer };

